import { Injectable, OnDestroy, inject } from "@angular/core";
import { BehaviorSubject, Subscription } from "rxjs";
import { AuthService } from "./auth.service";
import { LanguageCode } from "./language.model";
import { TranslationParams, translations } from "./translations";

const DEFAULT_LANGUAGE: LanguageCode = "en";
const GLOBAL_LANGUAGE_KEY = "lina:language";
const USER_LANGUAGE_KEY_PREFIX = "lina:language:";

function isLanguageCode(value: string | null): value is LanguageCode {
  return value === "en" || value === "pt" || value === "nl" || value === "sv";
}

function getStoredLanguage(userId?: string | null): LanguageCode {
  const userLanguage = userId
    ? window.localStorage.getItem(`${USER_LANGUAGE_KEY_PREFIX}${userId}`)
    : null;
  if (isLanguageCode(userLanguage)) return userLanguage;

  const globalLanguage = window.localStorage.getItem(GLOBAL_LANGUAGE_KEY);
  return isLanguageCode(globalLanguage) ? globalLanguage : DEFAULT_LANGUAGE;
}

function interpolate(template: string, params?: TranslationParams) {
  if (!params) return template;

  return Object.entries(params).reduce(
    (text, [key, value]) => text.replaceAll(`{${key}}`, String(value)),
    template,
  );
}

@Injectable({ providedIn: "root" })
export class LanguageService implements OnDestroy {
  private userId: string | null = null;
  private readonly subscription: Subscription;
  readonly language$ = new BehaviorSubject<LanguageCode>(getStoredLanguage());

  constructor() {
    const auth = inject(AuthService);

    this.subscription = auth.user$.subscribe((user) => {
      this.userId = user?.id || null;
      this.language$.next(getStoredLanguage(this.userId));
    });
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  get language() {
    return this.language$.value;
  }

  setLanguage(nextLanguage: LanguageCode) {
    this.language$.next(nextLanguage);
    window.localStorage.setItem(GLOBAL_LANGUAGE_KEY, nextLanguage);
    if (this.userId) {
      window.localStorage.setItem(
        `${USER_LANGUAGE_KEY_PREFIX}${this.userId}`,
        nextLanguage,
      );
    }
  }

  t(key: string, params?: TranslationParams) {
    const lang = this.language$.value;
    const table = translations[lang] as Record<string, string>;
    const fallback = translations.en as Record<string, string>;
    return interpolate(table[key] || fallback[key] || key, params);
  }
}
